import { useQuery } from "@tanstack/react-query";
import { client, shopV3Dataset } from "lib/sanity";
import {
  Faq,
  ShopCatalogV3,
  ShopPage,
  ShopPagePhaseTwo,
} from "lib/sanity.types";

interface QueryResult<T> {
  ms: number;
  query: string;
  result: T[];
}

export const useFetchShopPageQuery = ({ slug }: { slug: string }) =>
  useQuery(["fetchShopPage"], () =>
    client
      .fetch(`*[_type == "shopPages" && slug.current == "${slug}"]`)
      .then((shopPages: QueryResult<ShopPage>) => {
        if (shopPages.result.length === 0) return null;
        return shopPages.result[0];
      }),
  );

export const useFetchShopPagePhaseTwoQuery = ({ slug }: { slug: string }) =>
  useQuery(["fetchShopPagePhaseTwo"], () =>
    client
      .fetch(`*[_type == "shopPagesPhaseTwo" && slug.current == "${slug}"]`)
      .then((shopPages: QueryResult<ShopPagePhaseTwo>) => {
        if (shopPages.result.length === 0) return null;
        return shopPages.result[0];
      }),
  );

export const useFetchShopCatalogV3Query = ({
  subdomain,
}: {
  subdomain: string | undefined;
}) =>
  useQuery(
    ["fetchShopCatalogV3", subdomain],
    () => {
      // The catalog is keyed by tenant subdomain; sanitize before
      // interpolating into GROQ.
      const safeSubdomain = (subdomain ?? "").replace(/[^a-z0-9-]/g, "");
      return client
        .fetch(
          `*[_type == "shopCatalogV3" && subdomain == "${safeSubdomain}"]{
            ...,
            categories[]->{
              ...,
              products[]->
            }
          }`,
          shopV3Dataset,
        )
        .then((catalogs: QueryResult<ShopCatalogV3>) => {
          if (catalogs.result.length === 0) return null;
          return catalogs.result[0];
        });
    },
    { enabled: !!subdomain },
  );

export const useFetchFaqsQuery = ({
  slug,
  type = "shopPages",
}: {
  slug: string;
  type?: "shopPages" | "shopPagesPhaseTwo";
}) =>
  useQuery(["fetchFaqs"], async () => {
    const faqsUrls = await client.fetch(
      `*[_type == "${type}" && slug.current == "${slug}"] {
          "faqsUrl": faqs.asset->url
         }`,
    );
    if (faqsUrls.result.length === 0) return null;

    const url = faqsUrls.result[0].faqsUrl;
    if (url === null) return [] as Faq[];

    const faqs = await (await fetch(url)).json();
    return faqs as Faq[];
  });

interface AgreementDetails {
  name: string;
  url: string;
  _key: string;
}

interface Agreements {
  agreementDetails: AgreementDetails[];
  tenant: string;
}

export const useFetchAgreementsQuery = (tenant: string) => {
  return useQuery(["fetchAgreements"], async () => {
    const response: QueryResult<Agreements> = await client.fetch(
      `*[_type == "agreements" && tenant == "${tenant}"]`,
    );
    return response.result[0] || null;
  });
};
